/* =======================================================
   OGATU — HEADER JS v2.7 CLEAN FULL REPLACEMENT
   File: assets/section-ogatu-header.js
   ======================================================= */

(() => {
  const header = document.querySelector('[data-ogatu-header]');
  if (!header) return;

  const docEl = document.documentElement;

  const bar = header.querySelector('.ogatu-header__bar');
  const drawer = header.querySelector('[data-ogatu-drawer]');
  const search = header.querySelector('[data-ogatu-search]');

  const megaRoot = header.querySelector('[data-ogatu-mega-root]');
  const panelCanvas = header.querySelector('[data-ogatu-panel-canvas]');
  const panelCanvasHit = header.querySelector('[data-ogatu-panel-hit]');

  const ddPanels = header.querySelectorAll('[data-ogatu-dd-panel]');
  const mobileDots = header.querySelectorAll('.ogatu-cartdot--icon');
  const desktopDots = header.querySelectorAll('.ogatu-cartdot--text');

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isDesktop = () => window.matchMedia('(min-width: 990px)').matches;

  const state = {
    lastY: window.scrollY || 0,
    ticking: false,
    stuck: false,
    hidden: false,
    drawerOpen: false,
    searchOpen: false,
    megaOpen: false,
    ddOpen: false,
    hovered: false,
    closeTimer: null,
    pRAF: null,
    lastPointerEvent: null
  };

  const unlockScrollHard = () => {
    document.body.classList.remove(
      'overflow-hidden',
      'overflow-hidden-mobile',
      'overflow-hidden-tablet',
      'overflow-hidden-desktop'
    );

    document.documentElement.style.overflow = '';
    document.body.style.overflow = '';
  };

  const lockScroll = (lock) => {
    if (lock) {
      document.body.classList.add('overflow-hidden');
    } else {
      unlockScrollHard();
    }
  };

  const syncHeaderLayout = () => {
    const height = Math.round(bar?.offsetHeight || 60);
    docEl.style.setProperty('--ogatu-header-offset', `${height}px`);

    const overlayOn = header.getAttribute('data-overlay') === 'true';
    docEl.classList.toggle('ogatu-header-overlay', overlayOn);
    docEl.classList.toggle('ogatu-header-solid', !overlayOn);
  };

  const headerAttrObserver = new MutationObserver(() => {
    syncHeaderLayout();
  });

  const setDots = (count) => {
    const on = Number(count || 0) > 0;
    mobileDots.forEach((dot) => dot.classList.toggle('is-on', on));
    desktopDots.forEach((dot) => dot.classList.toggle('is-on', on));
  };

  const refreshCartCount = async () => {
    try {
      const res = await fetch('/cart.js', { credentials: 'same-origin' });
      if (!res.ok) return;
      const data = await res.json();
      setDots(data.item_count);
    } catch (_) {}
  };

  setDots(header.getAttribute('data-cart-count'));

  const getThreshold = () => {
    const height = bar ? bar.getBoundingClientRect().height : 60;
    return Math.max(60, Math.round(height));
  };

  const anyModalOpen = () => state.drawerOpen || state.searchOpen;

  const onScroll = () => {
    const y = window.scrollY || 0;
    const dy = y - state.lastY;
    const threshold = getThreshold();

    const shouldStuck = y > threshold;

    if (shouldStuck !== state.stuck) {
      state.stuck = shouldStuck;
      header.classList.toggle('is-stuck', shouldStuck);
      syncHeaderLayout();
    }

    if (!anyModalOpen() && !state.megaOpen && !state.ddOpen) {
      const shouldHide = shouldStuck && dy > 6;
      const shouldReveal = dy < -6;

      if (shouldHide && !state.hidden) {
        state.hidden = true;
        header.classList.add('is-hidden');
      } else if (shouldReveal && state.hidden) {
        state.hidden = false;
        header.classList.remove('is-hidden');
      }
    } else if (state.hidden) {
      state.hidden = false;
      header.classList.remove('is-hidden');
    }

    if (y <= 4 && state.hidden) {
      state.hidden = false;
      header.classList.remove('is-hidden');
    }

    state.lastY = y;
  };

  const requestTick = () => {
    if (state.ticking) return;

    state.ticking = true;
    window.requestAnimationFrame(() => {
      onScroll();
      state.ticking = false;
    });
  };

  window.addEventListener('scroll', requestTick, { passive: true });
  window.addEventListener('resize', syncHeaderLayout, { passive: true });
  window.addEventListener('load', syncHeaderLayout);

  window.addEventListener('pageshow', () => {
    syncHeaderLayout();
    refreshCartCount();

    if (
      !header.classList.contains('is-drawer-open') &&
      !header.classList.contains('is-search-open')
    ) {
      unlockScrollHard();
    }
  });

  headerAttrObserver.observe(header, {
    attributes: true,
    attributeFilter: ['data-overlay', 'class', 'style']
  });

  if (bar) {
    bar.addEventListener('mouseenter', () => {
      if (!isDesktop()) return;
      if (header.getAttribute('data-overlay') !== 'true') return;
      if (state.drawerOpen || state.searchOpen || state.megaOpen || state.ddOpen) return;

      state.hovered = true;
      header.classList.add('is-hovered');
      header.classList.remove('is-hidden');
    });

    bar.addEventListener('mouseleave', () => {
      state.hovered = false;
      header.classList.remove('is-hovered');
    });
  }

  window.addEventListener(
    'resize',
    () => {
      if (!isDesktop()) {
        state.hovered = false;
        header.classList.remove('is-hovered');
      }
    },
    { passive: true }
  );

  const clearCloseTimer = () => {
    if (!state.closeTimer) return;
    clearTimeout(state.closeTimer);
    state.closeTimer = null;
  };

  const setCanvas = (on) => {
    if (!panelCanvas) return;
    panelCanvas.hidden = !on;
  };

  const resetDropdownPanel = (panelEl) => {
    if (!panelEl) return;

    panelEl.querySelectorAll('[data-ogatu-dd-subtoggle]').forEach((btn) => {
      btn.setAttribute('aria-expanded', 'false');
    });

    panelEl.querySelectorAll('.ogatu-dd__sub').forEach((sub) => {
      sub.hidden = true;
    });
  };

  const closeAllMega = () => {
    header.querySelectorAll('[data-ogatu-mega-trigger]').forEach((trigger) => {
      trigger.setAttribute('aria-expanded', 'false');
    });

    megaRoot?.querySelectorAll('[data-ogatu-mega-panel]').forEach((panel) => {
      panel.hidden = true;
    });

    state.megaOpen = false;
    header.classList.remove('is-mega-open');
  };

  const closeAllDropdowns = () => {
    header.querySelectorAll('[data-ogatu-dd-trigger]').forEach((trigger) => {
      trigger.setAttribute('aria-expanded', 'false');
    });

    ddPanels.forEach((panel) => {
      resetDropdownPanel(panel);
      panel.hidden = true;
    });

    state.ddOpen = false;
    header.classList.remove('is-dd-open');
  };

  const closeAllPanels = () => {
    clearCloseTimer();
    closeAllMega();
    closeAllDropdowns();
    setCanvas(false);
  };

  const scheduleClosePanels = (delay = 140) => {
    clearCloseTimer();
    state.closeTimer = window.setTimeout(closeAllPanels, delay);
  };

  const openMega = (handle) => {
    if (!megaRoot || !handle) return;

    clearCloseTimer();
    closeAllDropdowns();

    const panel = megaRoot.querySelector(`[data-ogatu-mega-panel="${handle}"]`);
    if (!panel) return;

    closeAllMega();

    const trigger = header.querySelector(`[data-ogatu-mega-trigger="${handle}"]`);
    if (trigger) trigger.setAttribute('aria-expanded', 'true');

    panel.hidden = false;
    state.megaOpen = true;

    header.classList.add('is-mega-open');
    header.classList.remove('is-hidden', 'is-hovered');

    setCanvas(true);
  };

  const openDropdownForItem = (navItem) => {
    if (!navItem) return;

    clearCloseTimer();
    closeAllMega();

    const trigger = navItem.querySelector('[data-ogatu-dd-trigger]');
    const panel = navItem.querySelector('[data-ogatu-dd-panel]');
    if (!trigger || !panel) return;

    closeAllDropdowns();
    resetDropdownPanel(panel);

    trigger.setAttribute('aria-expanded', 'true');
    panel.hidden = false;

    state.ddOpen = true;
    header.classList.add('is-dd-open');
    header.classList.remove('is-hidden', 'is-hovered');

    setCanvas(true);
  };

  header.querySelectorAll('[data-ogatu-nav-item]').forEach((item) => {
    item.addEventListener('mouseenter', () => {
      if (!isDesktop()) return;

      const megaTrigger = item.querySelector('[data-ogatu-mega-trigger]');
      const ddTrigger = item.querySelector('[data-ogatu-dd-trigger]');

      if (megaTrigger) {
        openMega(megaTrigger.getAttribute('data-ogatu-mega-trigger'));
      } else if (ddTrigger) {
        openDropdownForItem(item);
      } else {
        closeAllPanels();
      }
    });

    item.addEventListener('mouseleave', () => {
      if (!isDesktop()) return;
      scheduleClosePanels(150);
    });
  });

  if (megaRoot) {
    megaRoot.addEventListener('mouseenter', clearCloseTimer);
    megaRoot.addEventListener('mouseleave', () => scheduleClosePanels(110));
  }

  ddPanels.forEach((panel) => {
    panel.addEventListener('mouseenter', clearCloseTimer);
    panel.addEventListener('mouseleave', () => scheduleClosePanels(110));
  });

  if (panelCanvasHit) {
    panelCanvasHit.addEventListener('mouseenter', () => {
      if (!isDesktop()) return;
      if (!state.megaOpen && !state.ddOpen) return;
      scheduleClosePanels(60);
    });

    panelCanvasHit.addEventListener('mouseleave', () => {
      if (!isDesktop()) return;
      clearCloseTimer();
    });

    panelCanvasHit.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      closeAllPanels();
    });
  } else if (panelCanvas) {
    panelCanvas.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      closeAllPanels();
    });
  }

  header.querySelectorAll('[data-ogatu-dd-subtoggle]').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();

      const expanded = btn.getAttribute('aria-expanded') === 'true';
      btn.setAttribute('aria-expanded', expanded ? 'false' : 'true');

      const row = btn.closest('.ogatu-dd__row');
      const sub = row ? row.querySelector('.ogatu-dd__sub') : null;
      if (sub) sub.hidden = expanded;
    });
  });

  header.querySelectorAll('.ogatu-dd__row').forEach((row) => {
    const parentLink = row.querySelector('.ogatu-dd__parent');
    const caretBtn = row.querySelector('[data-ogatu-dd-subtoggle]');
    const sub = row.querySelector('.ogatu-dd__sub');

    if (!parentLink || !caretBtn || !sub) return;

    parentLink.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();

      const expanded = caretBtn.getAttribute('aria-expanded') === 'true';
      caretBtn.setAttribute('aria-expanded', expanded ? 'false' : 'true');
      sub.hidden = expanded;
    });
  });

  document.addEventListener('click', (e) => {
    if (!header.contains(e.target)) closeAllPanels();
  });

  const pointerInsideOpenZone = (target) => {
    if (!target) return false;

    if (target.closest('[data-ogatu-panel-canvas]')) return false;
    if (target.closest('[data-ogatu-panel-hit]')) return false;

    if (bar && target.closest('.ogatu-header__bar')) return true;

    if (state.megaOpen && megaRoot && target.closest('[data-ogatu-mega-root]')) {
      if (panelCanvas && target.closest('[data-ogatu-panel-canvas]')) return false;
      return true;
    }

    if (target.closest('[data-ogatu-dd-panel]')) return true;

    return false;
  };

  const onPointerMove = (e) => {
    if (!isDesktop()) return;
    if (!state.megaOpen && !state.ddOpen) return;
    if (state.drawerOpen || state.searchOpen) return;

    state.lastPointerEvent = e;
    if (state.pRAF) return;

    state.pRAF = window.requestAnimationFrame(() => {
      state.pRAF = null;

      const ev = state.lastPointerEvent;
      if (!ev) return;

      const inside = pointerInsideOpenZone(ev.target);
      if (!inside) closeAllPanels();
    });
  };

  document.addEventListener('pointermove', onPointerMove, { passive: true });

  const stack = [];

  const setDrawerActiveLevel = (level, panelKey) => {
    if (!drawer) return;

    const levels = drawer.querySelectorAll('[data-ogatu-drawer-level]');
    levels.forEach((levelEl) => levelEl.classList.remove('is-active', 'is-left'));

    let active = null;

    if (level === '1') {
      active = drawer.querySelector('[data-ogatu-drawer-level="1"]');
    } else {
      active = drawer.querySelector(
        `[data-ogatu-drawer-level="${level}"][data-panel="${panelKey}"]`
      );
    }

    if (active) active.classList.add('is-active');
  };

  const openDrawer = () => {
    if (!drawer || state.drawerOpen) return;

    state.drawerOpen = true;
    closeAllPanels();

    header.classList.add('is-drawer-open');
    header.classList.remove('is-hidden', 'is-hovered');

    drawer.hidden = false;
    lockScroll(true);

    stack.length = 0;
    setDrawerActiveLevel('1', null);
  };

  const closeDrawer = () => {
    if (!drawer || !state.drawerOpen) return;

    state.drawerOpen = false;
    header.classList.remove('is-drawer-open');

    drawer.hidden = true;
    lockScroll(false);
  };

  header.querySelectorAll('[data-ogatu-drawer-toggle]').forEach((btn) => {
    btn.addEventListener('click', () => {
      if (state.drawerOpen) closeDrawer();
      else openDrawer();
    });
  });

  header.querySelectorAll('[data-ogatu-drawer-close]').forEach((el) => {
    el.addEventListener('click', closeDrawer);
  });

  drawer?.querySelectorAll('[data-ogatu-drawer-push]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const target = btn.getAttribute('data-target');
      if (!target) return;

      const currentActive = drawer.querySelector('.ogatu-drawer__level.is-active');
      const currentLevel = currentActive?.getAttribute('data-ogatu-drawer-level') || '1';
      const currentPanel = currentActive?.getAttribute('data-panel') || null;

      stack.push({
        level: currentLevel,
        panel: currentPanel
      });

      const hasLevel2 = drawer.querySelector(
        `[data-panel="${target}"][data-ogatu-drawer-level="2"]`
      );

      const next = hasLevel2
        ? { level: '2', panel: target }
        : { level: '3', panel: target };

      if (currentActive) currentActive.classList.add('is-left');
      setDrawerActiveLevel(next.level, next.panel);
    });
  });

  drawer?.querySelectorAll('[data-ogatu-drawer-back]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const prev = stack.pop();

      if (!prev) {
        setDrawerActiveLevel('1', null);
        return;
      }

      setDrawerActiveLevel(prev.level, prev.panel);

      drawer.querySelectorAll('[data-ogatu-drawer-level]').forEach((levelEl) => {
        levelEl.classList.remove('is-left');
      });
    });
  });

  const openSearch = () => {
    if (!search || state.searchOpen) return;

    state.searchOpen = true;
    closeAllPanels();

    header.classList.add('is-search-open');
    header.classList.remove('is-hidden', 'is-hovered');

    search.hidden = false;
    lockScroll(true);

    const input = search.querySelector('input[type="search"]');

    if (input) {
      window.setTimeout(() => {
        input.focus();
      }, prefersReducedMotion ? 0 : 30);
    }
  };

  const closeSearch = () => {
    if (!search || !state.searchOpen) return;

    state.searchOpen = false;
    header.classList.remove('is-search-open');

    search.hidden = true;
    lockScroll(false);
  };

  header.querySelectorAll('[data-ogatu-search-open]').forEach((btn) => {
    btn.addEventListener('click', openSearch);
  });

  header.querySelectorAll('[data-ogatu-search-close]').forEach((el) => {
    el.addEventListener('click', closeSearch);
  });

  const openCart = () => {
    const cartType = header.getAttribute('data-cart-type') || 'page';

    if (cartType !== 'drawer') {
      window.location.href = '/cart';
      return;
    }

    const cartDrawer = document.querySelector('cart-drawer');

    if (cartDrawer && typeof cartDrawer.open === 'function') {
      cartDrawer.open();
      return;
    }

    const bubble = document.getElementById('cart-icon-bubble');

    if (bubble) {
      bubble.click();
    } else {
      window.location.href = '/cart';
    }
  };

  header.querySelectorAll('[data-ogatu-cart-open]').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      openCart();
    });
  });

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;

    if (state.searchOpen) closeSearch();
    if (state.drawerOpen) closeDrawer();

    closeAllPanels();
    header.classList.remove('is-hovered');
  });

  const isCartMutationUrl = (url) => {
    if (!url) return false;

    const u = String(url);

    return (
      u.includes('/cart/add') ||
      u.includes('/cart/change') ||
      u.includes('/cart/update') ||
      u.includes('/cart/clear')
    );
  };

  const originalFetch = window.fetch;

  if (!window.__ogatuFetchPatched) {
    window.__ogatuFetchPatched = true;

    window.fetch = async (...args) => {
      const res = await originalFetch(...args);

      try {
        const url = args?.[0];

        if (isCartMutationUrl(url)) {
          window.setTimeout(() => {
            refreshCartCount();
          }, 80);
        }
      } catch (_) {}

      return res;
    };
  }

  syncHeaderLayout();
  onScroll();
})();